import { createContext, useContext, useMemo, useState } from "react"

import { cn } from "@/lib/utils"

const TabsContext = createContext(null)

export function Tabs({ children, defaultValue, onValueChange, value }) {
  const [internalValue, setInternalValue] = useState(defaultValue)
  const selectedValue = value ?? internalValue

  const contextValue = useMemo(() => ({
    value: selectedValue,
    setValue: (nextValue) => {
      if (value === undefined) setInternalValue(nextValue)
      onValueChange?.(nextValue)
    }
  }), [onValueChange, selectedValue, value])

  return <TabsContext.Provider value={contextValue}>{children}</TabsContext.Provider>
}

export function TabsList({ className, ...props }) {
  return (
    <div
      className={cn("flex gap-2 overflow-x-auto rounded-lg bg-[var(--color-panel)] p-1", className)}
      role="tablist"
      {...props}
    />
  )
}

export function TabsTrigger({ children, className, value, ...props }) {
  const context = useContext(TabsContext)
  const selected = context?.value === value

  return (
    <button
      aria-selected={selected}
      className={cn(
        "shrink-0 rounded-md px-4 py-2 text-sm font-medium transition-colors",
        selected
          ? "bg-white text-[var(--color-charcoal-blue)] shadow-[var(--shadow-soft)]"
          : "text-[var(--color-text-muted)] hover:bg-white/70 hover:text-[var(--color-charcoal-blue)]",
        className
      )}
      onClick={() => context?.setValue(value)}
      role="tab"
      type="button"
      {...props}
    >
      {children}
    </button>
  )
}

export function TabsContent({ children, className, value, ...props }) {
  const context = useContext(TabsContext)
  if (context?.value !== value) return null

  return (
    <div className={className} role="tabpanel" {...props}>
      {children}
    </div>
  )
}
